import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { maxLength } from 'utils/validation-utils'
import { validationContext } from 'validx'
import { Id } from 'types/definitions'
import { FindMembers } from 'iam/MemberStore'
import ClientInputEditorViewModel from './TransferToClient/ClientInputEditorViewModel'
import ProInputEditorViewModel from './TransferToPro/ProInputEditorViewModel'
import ClientTeamInputEdtorViewModel from './TransferToClientTeam/ClientTeamInputEditorViewModel'
import { FindUserTeamMembersByUserPublicId } from 'iam/TeamMemberStore'
import ProviderStore from 'jobs/ProviderStore'

export const enum TransferType {
  Pool = 'POOL',
  Pro = 'PRO',
  Client = 'CLIENT',
  Team = 'TEAM',
}

/**
 * Represents the shape of transfer job options.
 */
export interface TransferJobOption {
  displayName: string
  transferType: TransferType
}

/**
 * View Model for the Transfer Job dialog.
 * @extends ModalViewModel
 */
export default class TransferJobViewModel extends ModalViewModel {
  /**
   * Holds the maximum length of each field.
   */
  private readonly fieldLengths = {
    reason: { max: 100 },
  }

  /**
   * Validation context for the view model.
   */
  validation = validationContext(this, {
    reason: [maxLength(this.fieldLengths.reason.max)],
  })

  /**
   * Helper text for reason field by Transfer Type.
   */
  @computed
  get showReasonTextBox() {
    switch (this.transferType) {
      case TransferType.Client:
      case TransferType.Pro:
      case TransferType.Team:
        return `Why are you transferring?
        This text is public and can be seen by the Client and Pros.`
      default:
        return `Why are you transferring to the pool? This text is public
      and can be seen by the Client and Pros.`
    }
  }

  /**
   * Default option selection.
   */
  defaultOption: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
  ]

  /**
   * Consultation option selection.
   */
  consultationOptions: TransferJobOption[] = [
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
    {
      displayName: 'Pro',
      transferType: TransferType.Pro,
    },
  ]

  /**
   * Client and pool options selection.
   */
  poolAndClientOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
  ]

  /**
   * Client and team option selection.
   */
  unclaimedOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Team',
      transferType: TransferType.Team,
    },
  ]

  /**
   * Client and team option selection.
   */
  noProOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
    {
      displayName: 'Team',
      transferType: TransferType.Team,
    },
  ]

  /**
   * Client and team option selection.
   */
  noTeamOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
    {
      displayName: 'Pro',
      transferType: TransferType.Pro,
    },
  ]

  /**
   * Options for Transfer selection.
   */
  allOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
    {
      displayName: 'Pro',
      transferType: TransferType.Pro,
    },
    {
      displayName: 'Team',
      transferType: TransferType.Team,
    },
  ]

  /**
   * Transfer options depending on permission.
   */
  @computed
  get filteredTransferOptions() {
    const poolTransferOption = this.job.canTransferToPoolAsAdmin
    const clientTransferOption = this.job.canTransferToClientAsAdmin
    const proTransferOption = this.job.canTransferToProAsAdmin
    const clientTeamTransferOption = this.job.canTransferToClientTeamAsAdmin
    const canSeeAllOptions =
      clientTransferOption &&
      proTransferOption &&
      clientTeamTransferOption &&
      poolTransferOption
    const canSeeAllExceptProOptions =
      !proTransferOption &&
      clientTransferOption &&
      clientTeamTransferOption &&
      poolTransferOption
    const canSeeAllExceptTeamOptions =
      proTransferOption &&
      clientTransferOption &&
      !clientTeamTransferOption &&
      poolTransferOption

    // If we have a consultation only allow transfer to the pool and provider.
    if (this.job.hasActiveConsultation) {
      return this.consultationOptions
    }

    // We check these just in case.
    if (this.job.isClaimedJob && canSeeAllExceptProOptions) {
      return this.noProOptions
    }

    if (this.job.isClaimedJob && canSeeAllExceptTeamOptions) {
      return this.noTeamOptions
    }

    // Display all transfer options.
    if (this.job.isClaimedJob && canSeeAllOptions) {
      return this.allOptions
    }

    // Job on hold with champion provider still shows all transfer options.
    if (
      this.job.status === 'ON_HOLD' &&
      this.job.championProvider &&
      canSeeAllOptions
    ) {
      return this.allOptions
    }

    // In this case, we only show client/team transfer options.
    if (
      !this.job.isClaimedJob &&
      clientTeamTransferOption &&
      clientTransferOption
    ) {
      return this.unclaimedOptions
    }

    if (this.job.isClaimedJob && poolTransferOption && clientTransferOption) {
      return this.poolAndClientOptions
    }

    return this.defaultOption
  }

  /**
   * Registry of detail view models for each possible type of transfer.
   */
  readonly transferViewModels = {
    [TransferType.Client]: new ClientInputEditorViewModel(
      this.job,
      this.findMembers
    ),
    [TransferType.Pro]: new ProInputEditorViewModel(
      this.job,
      this.findMembers,
      this.providerStore
    ),
    [TransferType.Team]: new ClientTeamInputEdtorViewModel(
      this.job,
      this.findTeamMembersByUserPublicId
    ),
    [TransferType.Pool]: null,
  } as const

  /**
   * The transfer type.
   */
  @observable
  transferType: TransferType = TransferType.Client

  /**
   * The reason of the transfer.
   */
  @observable
  reason = ''

  /**
   * Disables transfer button.
   */
  @observable
  disabled = true

  /**
   * The currently selected transfer type.
   */
  @computed
  get option() {
    return this.allOptions.find((o) => o.transferType === this.transferType)
  }

  @computed
  get showRateProButton() {
    return this.canRatePro()
  }

  constructor(
    private job: Job,
    private findMembers: FindMembers,
    private findTeamMembersByUserPublicId: FindUserTeamMembersByUserPublicId,
    private jobStore: JobStore,
    private providerStore: ProviderStore,
    private newAlert: AlertFactory,
    private fetchJobRatingStatus: () => Promise<void>,
    private canRatePro: () => boolean,
    public showProRatingDialog: () => void
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)
    this.show$.subscribe(this.shown)
  }

  /**
   * Shows Transfer Job Dialog.
   */
  show() {
    if (this.showing) {
      return
    }

    if (this.job.hasActiveConsultation) {
      this.transferType = TransferType.Pool
    }

    super.show()
  }

  /**
   * Transfers job by Transfer Type. It could be to the Pool, Pro, Client or Team.
   */
  transfer = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    switch (this.transferType) {
      case TransferType.Pool:
        await this.transferJobToPool()
        break
      case TransferType.Client:
        await this.transferJobToClient()
        break
      case TransferType.Pro:
        await this.transferJobToPro()
        break
      case TransferType.Team:
        await this.transferJobToClientTeam()
        break
      default:
        return Promise.reject(new Error('Transfer type not supported.'))
    }

    this.close()
  })

  /**
   * Transfers job to the pool.
   */
  async transferJobToPool() {
    await this.jobStore.transferJobToPoolAsAdmin(
      Id.assert(this.job.id),
      this.reason
    )

    this.newAlert()
      .withState(AlertState.Success)
      .saying('Success', 'Job transferred back to the pool.')
      .autoDismiss()
      .show()
  }

  /**
   * Transfer to client.
   */
  transferJobToClient = task.resolved(async () => {
    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Transferring to new client',
        `Transferring ${
          this.transferViewModels[TransferType.Client].client?.displayName
        } to ${this.job.name}...`
      )
      .autoDismiss()
      .show()

    await this.jobStore.transferJobToClientAsAdmin(
      Id.assert(this.job.id),
      Id.assert(
        this.transferViewModels[TransferType.Client].client?.user.publicId
      ),
      this.reason
    )
    alert
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `${
          this.transferViewModels[TransferType.Client].client?.displayName
        } was transferred to ${this.job.name}.`
      )
      .autoDismiss()
  })

  /**
   * Transfer to pro.
   */
  transferJobToPro = task.resolved(async () => {
    const proVM = this.transferViewModels[TransferType.Pro]

    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Transferring to new pro',
        `Transferring ${proVM.pro?.displayName} to ${this.job.name}...`
      )
      .autoDismiss()
      .show()

    await this.jobStore.transferJobToProAsAdmin(
      Id.assert(this.job.id),
      Id.assert(proVM.pro?.user.publicId),
      this.reason
    )
    alert
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `${proVM.pro?.displayName} was transferred to ${this.job.name}.`
      )
      .autoDismiss()
  })

  /**
   * Transfer to client team.
   */
  transferJobToClientTeam = task.resolved(async () => {
    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Transferring to new client team',
        `Transferring ${this.job.name} to ${
          this.transferViewModels[TransferType.Team].clientTeamMember?.team.name
        }...`
      )
      .autoDismiss()
      .show()

    await this.jobStore.transferJobToClientTeamAsAdmin(
      Id.assert(this.job.id),
      Id.assert(
        this.transferViewModels[TransferType.Team].clientTeamMember?.team.id
      ),
      this.reason
    )
    alert
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `${this.job.name} was transferred to ${
          this.transferViewModels[TransferType.Team].clientTeamMember?.team.name
        }.`
      )
      .autoDismiss()
  })

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    const isThisViewModelValid = this.validation.reset().validate().isValid
    const isClientViewModelValid =
      this.transferViewModels[TransferType.Client].validateClient()
    const isProViewModelValid =
      this.transferViewModels[TransferType.Pro].validate()
    const isClientTeamViewModelValid =
      this.transferViewModels[TransferType.Team].validateClientTeamMember()

    switch (this.transferType) {
      case TransferType.Client:
        return isThisViewModelValid && isClientViewModelValid
      case TransferType.Pro:
        return isThisViewModelValid && isProViewModelValid
      case TransferType.Team:
        return isThisViewModelValid && isClientTeamViewModelValid
      default:
        return isThisViewModelValid
    }
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.reason = ''
    this.disabled = true
    this.transferType = TransferType.Client
    this.transfer.reset()

    this.validation.reset()
    Object.values(this.transferViewModels).forEach((vm) => vm?.reset())
    Object.values(this.transferViewModels).forEach((vm) => vm?.deactivate())
  }

  /**
   * Activates the member search field with a debounce time.
   */
  @action.bound
  shown() {
    Object.values(this.transferViewModels).forEach((vm) => vm?.activate())
  }

  /**
   * Sets the transfer type.
   */
  @action.bound
  async setTransferOption(transferOption?: TransferJobOption) {
    if (transferOption) {
      if (transferOption.transferType === TransferType.Pro) {
        this.fetchJobRatingStatus().then()
      }

      this.transferType = transferOption.transferType
    }
  }
}
